import { create, StateCreator } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { LlmType } from "./constants";
import { Account, DataConfigurations } from "@/types";
import {
  CreativeReportListItem,
  SyncAssetsResponse,
  InsightPrompt,
  InsightType,
} from "./screens/creatives/types";
import { StreamState } from "./services/backgroundStreamManager";
import { TZDate } from "@date-fns/tz";
import { syncCreativeAssets } from "@/screens/creatives/apiService";
import { formatForAPI, nowInTimezone } from "@/utils/dateUtils";
import { fromZonedTime } from "date-fns-tz";

export interface UserProfileSlice {
  userName: string;
  userEmail: string;
  userCompany?: string;
  userRole?: string;
  llmType: LlmType;
  profilePicture: string | null;
  supportHash: string | null;
  freeTrialExpired: boolean;
  permission: {
    [key: string]:
      | boolean
      | {
          [key: string]: boolean;
        };
  };
  setUserProfile: (
    userProfile: Partial<Omit<UserProfileSlice, "setUserProfile">>,
  ) => void;
}

export const createUserProfileSlice: StateCreator<UserProfileSlice, [], []> = (set) => ({
  userName: "",
  userEmail: "",
  userCompany: "",
  userRole: "",
  llmType: LlmType.Anthropic,
  profilePicture: null,
  freeTrialExpired: false,
  permission: {},
  supportHash: null,
  setUserProfile: (userProfile) => set({ ...userProfile }),
});

export interface AccountSlice {
  isInitializingAccounts: boolean;
  setIsInitializingAccounts: (isFirstLoading: boolean) => void;
  totalAccountPages: number;
  setTotalAccountPages: (totalAccountPages: number) => void;
  currentAccountPage: number;
  setCurrentAccountPage: (currentAccountPage: number) => void;
  accountList: Account[];
  setAccountList: (accounts: Account[]) => void;
  selectedAccount: Account | undefined;
  setSelectedAccount: (account: Account | undefined) => void;
  noAccountsAvailable: boolean;
  setNoAccountsAvailable: (noAccountsAvailable: boolean) => void;
}

export interface OauthConnectionSlice {
  isOauthConnecting: boolean;
  oauthConnectingPlatformLabel: string | null;
  setOauthConnectingState: (state: {
    isConnecting: boolean;
    platformLabel: string | null;
  }) => void;
}

// Module-level timeout ref for sync polling
let syncTimeoutRef: NodeJS.Timeout | undefined;
let currentSyncAccountId: string | undefined;

export const createAccountSlice: StateCreator<
  AccountSlice & CreativesSlice & OauthConnectionSlice,
  [],
  [],
  AccountSlice
> = (set, get) => ({
  isInitializingAccounts: true,
  setIsInitializingAccounts: (isInitializingAccounts) => set({ isInitializingAccounts }),
  totalAccountPages: 0,
  setTotalAccountPages: (totalAccountPages) => set({ totalAccountPages }),
  currentAccountPage: 1,
  setCurrentAccountPage: (currentAccountPage) => set({ currentAccountPage }),
  accountList: [],
  setAccountList: (accounts) => set({ accountList: accounts }),
  selectedAccount: undefined,
  setSelectedAccount: (account) => {
    set({ selectedAccount: account });
    // Trigger sync when account changes
    const state = get();
    if ("syncAccountAssets" in state && typeof state.syncAccountAssets === "function") {
      state.setIsAccountSynced(false);
      state.setAccountSyncStatus(null);
      void state.syncAccountAssets(account, false);
    }
  },
  noAccountsAvailable: false,
  setNoAccountsAvailable: (noAccountsAvailable) => set({ noAccountsAvailable }),
});

export const createOauthConnectionSlice: StateCreator<OauthConnectionSlice, [], []> = (set) => ({
  isOauthConnecting: false,
  oauthConnectingPlatformLabel: null,
  setOauthConnectingState: ({ isConnecting, platformLabel }) =>
    set({
      isOauthConnecting: isConnecting,
      oauthConnectingPlatformLabel: platformLabel,
    }),
});

interface VisibleAdName {
  fullName: string;
  sections: string[];
}

export interface CreativesSlice {
  creativeReportList: CreativeReportListItem[];
  setCreativeReportList: (list: CreativeReportListItem[]) => void;
  isCreativeListLoading: boolean;
  setIsCreativeListLoading: (loading: boolean) => void;
  selectedCreativeReport: CreativeReportListItem | null;
  setSelectedCreativeReport: (report: CreativeReportListItem | null) => void;
  dataConfigurations: DataConfigurations | null;
  setDataConfigurations: (data: DataConfigurations | null) => void;
  resetCreativeStoreState: () => void;
  isAccountSynced: boolean;
  setIsAccountSynced: (synced: boolean) => void;
  accountSyncStatus: SyncAssetsResponse | null;
  setAccountSyncStatus: (status: SyncAssetsResponse | null) => void;
  allowedDateRange: {
    first_allowed_date: TZDate;
    last_allowed_date: TZDate;
  } | null;
  setAllowedDateRange: (
    dateRange: {
      first_allowed_date: TZDate;
      last_allowed_date: TZDate;
    } | null,
  ) => void;
  visibleAdNamesBySection: Record<string, VisibleAdName[]>;
  setVisibleAdNamesBySection: (sectionId: string, adNames: VisibleAdName[]) => void;
  clearVisibleAdNames: () => void;
  isAutoSaving: boolean;
  setIsAutoSaving: (isAutoSaving: boolean) => void;
  // Color assignment system - section-wise (assigns distinct colors to visible ads per page)
  adNameColorMapBySection: Record<string, Record<string, string>>;
  assignColorsToVisibleAds: (sectionId: string, visibleAdNames: string[]) => void;
  getAdNameColor: (sectionId: string, adName: string) => string | undefined;
  clearSectionColors: (sectionId: string) => void;
  // Insight prompts - account-specific, stored by type
  insightPrompts: Record<InsightType, InsightPrompt[]>;
  setInsightPrompts: (type: InsightType, prompts: InsightPrompt[]) => void;
  getInsightPrompts: (type: InsightType) => InsightPrompt[];
  clearInsightPrompts: () => void;
  // Account sync management
  syncAccountAssets: (account: Account | undefined, isPolling?: boolean) => Promise<void>;
  clearSyncTimeout: () => void;
}

// Color palette for consistent assignment
const LINE_COLORS = [
  "#FF1F5C",
  "#3CB9FF",
  "#4318FF",
  "#039F54",
  "#F1AE03",
  "#FC7AFF",
  "#00B2B2",
  "#FF966F",
];

export const createCreativesSlice: StateCreator<
  CreativesSlice & AccountSlice & OauthConnectionSlice,
  [],
  [],
  CreativesSlice
> = (set, get) => ({
  creativeReportList: [],
  setCreativeReportList: (list) => set({ creativeReportList: list }),
  isCreativeListLoading: true,
  setIsCreativeListLoading: (loading) => set({ isCreativeListLoading: loading }),
  selectedCreativeReport: null,
  setSelectedCreativeReport: (report) => set({ selectedCreativeReport: report }),
  dataConfigurations: null,
  setDataConfigurations: (data) => set({ dataConfigurations: data }),
  isAccountSynced: false,
  setIsAccountSynced: (synced) => set({ isAccountSynced: synced }),
  accountSyncStatus: null,
  setAccountSyncStatus: (status) => set({ accountSyncStatus: status }),
  allowedDateRange: null,
  setAllowedDateRange: (dateRange) => set({ allowedDateRange: dateRange }),
  visibleAdNamesBySection: {},
  setVisibleAdNamesBySection: (sectionId, adNames) =>
    set((state) => ({
      visibleAdNamesBySection: {
        ...state.visibleAdNamesBySection,
        [sectionId]: adNames,
      },
    })),
  clearVisibleAdNames: () => set({ visibleAdNamesBySection: {} }),
  resetCreativeStoreState: () =>
    set({
      selectedCreativeReport: null,
      creativeReportList: [],
      dataConfigurations: null,

      visibleAdNamesBySection: {},
      isAutoSaving: false,
      isCreativeListLoading: true,
      adNameColorMapBySection: {},
      insightPrompts: {
        [InsightType.SectionInsights]: [],
        [InsightType.CompetitorInsights]: [],
      },
    }),
  isAutoSaving: false,
  setIsAutoSaving: (isAutoSaving) => set({ isAutoSaving }),
  // Color assignment system - section-wise (assigns distinct colors to visible ads per page)
  adNameColorMapBySection: {},
  // Assign colors to visible ad names on the current page
  // Colors are reassigned each time the page changes to ensure no two ads on the same page share a color
  assignColorsToVisibleAds: (sectionId: string, visibleAdNames: string[]) => {
    set((state) => {
      // Create a fresh color map for this section based on current visible ads
      const newColorMap: Record<string, string> = {};

      // Assign colors based on position in the visible list (current page)
      // This ensures each visible ad gets a distinct color (up to LINE_COLORS.length)
      visibleAdNames.forEach((adName, index) => {
        newColorMap[adName] = LINE_COLORS[index % LINE_COLORS.length];
      });

      return {
        adNameColorMapBySection: {
          ...state.adNameColorMapBySection,
          [sectionId]: newColorMap,
        },
      };
    });
  },
  // Get color for ad name in a specific section (read-only)
  getAdNameColor: (sectionId: string, adName: string) => {
    const state = get();
    return state.adNameColorMapBySection[sectionId]?.[adName];
  },
  // Clear colors for a specific section
  clearSectionColors: (sectionId: string) => {
    set((state) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [sectionId]: _, ...rest } = state.adNameColorMapBySection;
      return {
        adNameColorMapBySection: rest,
      };
    });
  },
  // Insight prompts - account-specific, stored by type
  insightPrompts: {
    [InsightType.SectionInsights]: [],
    [InsightType.CompetitorInsights]: [],
  },
  setInsightPrompts: (type, prompts) =>
    set((state) => ({
      insightPrompts: {
        ...state.insightPrompts,
        [type]: prompts,
      },
    })),
  getInsightPrompts: (type) => {
    const state = get();
    return state.insightPrompts[type] || [];
  },
  clearInsightPrompts: () =>
    set({
      insightPrompts: {
        [InsightType.SectionInsights]: [],
        [InsightType.CompetitorInsights]: [],
      },
    }),
  // Clear sync timeout
  clearSyncTimeout: () => {
    if (syncTimeoutRef) {
      clearTimeout(syncTimeoutRef);
      syncTimeoutRef = undefined;
    }
    currentSyncAccountId = undefined;
  },
  // Sync account assets - called automatically on account change
  syncAccountAssets: async (account: Account | undefined, isPolling = false) => {
    // Clear any existing timeout
    if (syncTimeoutRef) {
      clearTimeout(syncTimeoutRef);
      syncTimeoutRef = undefined;
    }

    const accountId = account?.id;
    if (!accountId) {
      // Only update state if not polling
      if (!isPolling) {
        set({
          isAccountSynced: false,
          accountSyncStatus: null,
          allowedDateRange: null,
        });
      }
      currentSyncAccountId = undefined;
      return;
    }

    // Store the account ID we're syncing for
    currentSyncAccountId = accountId;

    try {
      const date = nowInTimezone(account.time_zone ?? "UTC");
      const formattedDate = formatForAPI(date);

      const response = await syncCreativeAssets(accountId, formattedDate);

      // Check if account changed during the API call
      if (currentSyncAccountId !== accountId) {
        return;
      }

      // Always update accountSyncStatus (for both first call and polling)
      set({ accountSyncStatus: response });

      // Only update other state if not polling (first API response only)
      if (!isPolling) {
        set({ isAccountSynced: true });

        if (response && response.first_synced_date && response.last_synced_date) {
          const timezone = account.time_zone || "UTC";
          try {
            const startDate = new TZDate(
              fromZonedTime(response.first_synced_date, timezone),
              timezone,
            );
            startDate.setHours(0, 0, 0, 0);
            const endDate = new TZDate(
              fromZonedTime(response.last_synced_date, timezone),
              timezone,
            );
            endDate.setHours(23, 59, 59, 999);
            if (startDate && endDate) {
              set({
                allowedDateRange: {
                  first_allowed_date: startDate,
                  last_allowed_date: endDate,
                },
              });
            }
          } catch (e) {
            console.error("Error converting timezone:", e);
          }
        }
      }

      // Set up polling if status is pending
      if (response?.status === "pending" && currentSyncAccountId === accountId) {
        syncTimeoutRef = setTimeout(() => {
          const state = get();
          const currentAccount = (state as AccountSlice).selectedAccount;
          const syncFn = (state as CreativesSlice).syncAccountAssets;
          if (currentAccount?.id === accountId && syncFn) {
            void syncFn(currentAccount, true);
          }
        }, 5000);
      }
    } catch (error) {
      // Check if account changed before setting error states
      if (currentSyncAccountId !== accountId) {
        return;
      }

      console.error("Error syncing account assets:", error);
      // Only update state if not polling
      if (!isPolling) {
        set({
          isAccountSynced: false,
          accountSyncStatus: null,
          allowedDateRange: null,
        });
      }
    }
  },
});

export interface SidebarSlice {
  sidebarWidth: number;
  setSidebarWidth: (width: number) => void;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
}

export const createSidebarSlice: StateCreator<SidebarSlice, [], []> = (set) => ({
  sidebarWidth: 200, // Default width
  setSidebarWidth: (width) => set({ sidebarWidth: width }),
  sidebarCollapsed: false,
  setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
});

export interface UnsavedChangesSlice {
  unsavedChangesChecker: (() => boolean) | null;
  setUnsavedChangesChecker: (checker: (() => boolean) | null) => void;
  pendingAccount: Account | null;
  setPendingAccount: (account: Account | null) => void;
}

export const createUnsavedChangesSlice: StateCreator<UnsavedChangesSlice, [], []> = (
  set,
) => ({
  unsavedChangesChecker: null,
  setUnsavedChangesChecker: (checker) => set({ unsavedChangesChecker: checker }),
  pendingAccount: null,
  setPendingAccount: (account) => set({ pendingAccount: account }),
});

export interface BackgroundStreamsSlice {
  activeStreams: Map<string, StreamState>;
  addStream: (id: string, stream: StreamState) => void;
  removeStream: (id: string) => void;
  clearAllStreams: () => void;
  getStreamById: (id: string) => StreamState | undefined;
  getStreamsByAccount: (accountId: string) => StreamState[];
  getStreamsByReport: (accountId: string, reportId: string) => StreamState[];
  getStreamByChatId: (chatId: string) => StreamState | undefined;
}

export const createBackgroundStreamsSlice: StateCreator<
  BackgroundStreamsSlice,
  [],
  []
> = (set, get) => ({
  activeStreams: new Map<string, StreamState>(),
  addStream: (id, stream) =>
    set((state) => {
      const newMap = new Map(state.activeStreams);
      newMap.set(id, stream);
      return { activeStreams: newMap };
    }),
  removeStream: (id) =>
    set((state) => {
      const newMap = new Map(state.activeStreams);
      newMap.delete(id);
      return { activeStreams: newMap };
    }),
  clearAllStreams: () => set({ activeStreams: new Map() }),
  getStreamById: (id) => get().activeStreams.get(id),
  getStreamsByAccount: (accountId: string) => {
    return Array.from(get().activeStreams.values()).filter(
      (stream) => stream.accountId === accountId,
    );
  },
  getStreamsByReport: (accountId: string, reportId: string) => {
    return Array.from(get().activeStreams.values()).filter(
      (stream) => stream.accountId === accountId && stream.reportId === reportId,
    );
  },
  getStreamByChatId: (chatId: string) => {
    return Array.from(get().activeStreams.values()).find(
      (stream) => stream.chatId === chatId,
    );
  },
});

export interface NavigationSelectionsSlice {
  // Last URL per tab for current account: { [tabKey]: lastUrl }
  lastUrls: Record<string, string>;
  getLastUrl: (tabKey: string) => string | null;
  setLastUrl: (tabKey: string, url: string) => void;
  clearAllUrls: () => void;
}

export const createNavigationSelectionsSlice: StateCreator<
  NavigationSelectionsSlice,
  [],
  [],
  NavigationSelectionsSlice
> = (set, get) => ({
  lastUrls: {},
  getLastUrl: (tabKey) => {
    return get().lastUrls[tabKey] ?? null;
  },
  setLastUrl: (tabKey, url) =>
    set((state) => ({
      lastUrls: {
        ...state.lastUrls,
        [tabKey]: url,
      },
    })),
  clearAllUrls: () => set({ lastUrls: {} }),
});

type AllSlice = UserProfileSlice &
  AccountSlice &
  OauthConnectionSlice &
  CreativesSlice &
  SidebarSlice &
  UnsavedChangesSlice &
  BackgroundStreamsSlice &
  NavigationSelectionsSlice;

export const useGlobalStore = create<AllSlice>()(
  persist(
    (...a) => ({
      ...createUserProfileSlice(...a),
      ...createAccountSlice(...a),
      ...createOauthConnectionSlice(...a),
      ...createCreativesSlice(...a),
      ...createSidebarSlice(...a),
      ...createUnsavedChangesSlice(...a),
      ...createBackgroundStreamsSlice(...a),
      ...createNavigationSelectionsSlice(...a),
    }),
    {
      name: "sidebar-storage",
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        sidebarWidth: state.sidebarWidth,
        adNameColorMapBySection: state.adNameColorMapBySection, // Persist color assignments per section
        lastUrls: state.lastUrls, // Persist last URLs per tab for current account
      }),
    },
  ),
);
